/* RD Customz — global module (every page). No framework, no dependencies.
   Dialogs (native <dialog>), mobile navigation panel, language disclosure, cart drawer + cart page updates through the
   Section Rendering API, analytics hooks. Server-rendered HTML stays the source of truth; this file only upgrades interaction. */

const cfgNode = document.getElementById('rdc-config');
const RDC = cfgNode ? JSON.parse(cfgNode.textContent) : { routes: {}, strings: {}, modules: {} };
window.RDC = RDC;

/* ------------------------------------------------------------ analytics hook (optional by design, MP06 §78/§79) */
function track(name, payload = {}) {
  try {
    window.Shopify?.analytics?.publish?.(`rdc:${name}`, payload);
  } catch (e) { /* analytics must never break the UI */ }
}
window.rdcTrack = track;

/* ------------------------------------------------------------ live announcements */
const live = document.getElementById('rdc-live');
function announce(text) {
  if (!live || !text) return;
  live.textContent = '';
  requestAnimationFrame(() => { live.textContent = text; });
}

/* ------------------------------------------------------------ dialogs */
const openers = new WeakMap();

// EU guarantee notice: fit the whole A4 notice to the screen, or show it at about natural size with free panning
function setEuZoom(dialog, on) {
  if (!dialog) return;
  dialog.toggleAttribute('data-zoom', on);
  const btn = dialog.querySelector('[data-rdc-eu-zoom]');
  if (btn) { btn.setAttribute('aria-pressed', String(on)); btn.textContent = on ? btn.dataset.labelOut : btn.dataset.labelIn; }
  const body = dialog.querySelector('.rdc-eu-notice__scroll');
  if (body) { body.scrollLeft = 0; if (!on) body.scrollTop = 0; }
}

function openDialog(dialog, opener = document.activeElement) {
  if (!dialog || dialog.open) return;
  openers.set(dialog, opener);
  if (typeof dialog.showModal === 'function') dialog.showModal();
  else dialog.setAttribute('open', '');
  const focusTarget = dialog.querySelector('[data-rdc-autofocus]') || dialog.querySelector('input[type="search"]');
  if (focusTarget) focusTarget.focus();
  dialog.dispatchEvent(new CustomEvent('rdc:dialog-open', { bubbles: true }));
}

function closeDialog(dialog) {
  if (!dialog || !dialog.open) return;
  if (typeof dialog.close === 'function') dialog.close();
  else dialog.removeAttribute('open');
}

document.addEventListener('close', (event) => {
  const dialog = event.target;
  if (!(dialog instanceof HTMLDialogElement)) return;
  const opener = openers.get(dialog);
  if (opener && document.contains(opener) && typeof opener.focus === 'function') opener.focus();
}, true);

// Esc always closes the top modal dialog, also when focus is in a search field (which otherwise only clears its text)
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const open = [...document.querySelectorAll('dialog[open]')].pop();
  if (open) { event.preventDefault(); closeDialog(open); }
});

document.addEventListener('click', (event) => {
  const t = event.target;
  // backdrop click on a modal dialog closes it
  if (t instanceof HTMLDialogElement && t.open) {
    const r = t.getBoundingClientRect();
    const inside = event.clientX >= r.left && event.clientX <= r.right && event.clientY >= r.top && event.clientY <= r.bottom;
    if (!inside) { closeDialog(t); return; }
  }
  const close = t.closest('[data-rdc-close]');
  if (close) { closeDialog(close.closest('dialog')); return; }

  const opener = t.closest('[data-rdc-open-dialog]');
  if (opener) {
    event.preventDefault();
    openDialog(document.getElementById(opener.dataset.rdcOpenDialog), opener);
    if (opener.dataset.rdcEvent) track(opener.dataset.rdcEvent, eventPayload(opener));
    return;
  }
  const eu = t.closest('[data-rdc-eu-notice]');
  if (eu) {
    const dialog = document.getElementById('rdc-eu-notice');
    if (dialog) {
      event.preventDefault();
      const img = dialog.querySelector('img[data-src]');
      if (img && !img.getAttribute('src')) img.setAttribute('src', img.dataset.src);
      setEuZoom(dialog, false);
      openDialog(dialog, eu);
      track('policy_opened', { policy: 'legal_guarantee' });
    }
    return;
  }
  const zoom = t.closest('[data-rdc-eu-zoom]');
  if (zoom) {
    setEuZoom(zoom.closest('dialog'), zoom.getAttribute('aria-pressed') !== 'true');
    return;
  }
  const search = t.closest('[data-rdc-search-open]');
  if (search) {
    const dialog = document.getElementById('rdc-search');
    if (dialog) {
      event.preventDefault();
      openDialog(dialog, search);
      import(RDC.modules.search).then((m) => m.init(dialog)).catch(() => {});
      track('search_opened', { surface: 'header' });
    }
    return;
  }
  const cart = t.closest('[data-rdc-cart-open]');
  if (cart && !document.querySelector('[data-rdc-cart-page]')) {
    const dialog = document.getElementById('rdc-cart');
    if (dialog) {
      event.preventDefault();
      openDialog(dialog, cart);
      track('cart_opened', { line_count: Number(dialog.dataset.count || 0) });
    }
    return;
  }
  const tracked = t.closest('[data-rdc-event]');
  if (tracked) track(tracked.dataset.rdcEvent, eventPayload(tracked));
});

function eventPayload(el) {
  const payload = { surface: el.dataset.surface || document.body.className.replace('rdc-t-', '') };
  if (el.dataset.brand) payload.brand = el.dataset.brand;
  if (el.dataset.channel) payload.channel = el.dataset.channel;
  return payload;
}

/* ------------------------------------------------------------ focus trap for non-dialog panels (nav, filters) */
const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

function trapFocus(container, event) {
  if (event.key !== 'Tab') return;
  const items = [...container.querySelectorAll(FOCUSABLE)].filter((n) => n.offsetParent !== null || n === document.activeElement);
  if (!items.length) return;
  const first = items[0];
  const last = items[items.length - 1];
  if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
  else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
}

function setBackgroundInert(except, inert) {
  document.querySelectorAll('body > *').forEach((n) => {
    if (n.contains(except) || n.tagName === 'SCRIPT' || n.tagName === 'DIALOG') return;
    if (inert) n.setAttribute('inert', ''); else n.removeAttribute('inert');
  });
}

/* ------------------------------------------------------------ navigation drawer (hamburger at every width, owner 17-09-2026) */
const nav = document.querySelector('[data-rdc-nav]');
if (nav) {
  const panel = nav.querySelector('.rdc-nav__panel');
  const openBtn = document.querySelector('[data-rdc-nav-open]');
  const header = nav.closest('header');

  const setOpen = (open) => {
    nav.dataset.open = String(open);
    openBtn?.setAttribute('aria-expanded', String(open));
    if (open) {
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
      document.documentElement.classList.add('rdc-lock');
      setBackgroundInert(header, true);
      header.querySelectorAll('.rdc-header__inner > :not(.rdc-nav)').forEach((n) => n.setAttribute('inert', ''));
      panel.querySelector(FOCUSABLE)?.focus();
    } else {
      panel.removeAttribute('role');
      panel.removeAttribute('aria-modal');
      document.documentElement.classList.remove('rdc-lock');
      setBackgroundInert(header, false);
      header.querySelectorAll('.rdc-header__inner > [inert]').forEach((n) => n.removeAttribute('inert'));
    }
  };
  openBtn?.addEventListener('click', () => { setOpen(true); track('menu_opened', {}); });
  nav.addEventListener('click', (e) => {
    if (e.target.closest('[data-rdc-nav-close]')) { setOpen(false); openBtn?.focus(); }
    const toggle = e.target.closest('[data-rdc-sub-toggle]');
    if (toggle) {
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!expanded));
    }
  });
  nav.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (nav.dataset.open === 'true') { setOpen(false); openBtn?.focus(); return; }
      const open = nav.querySelector('[data-rdc-sub-toggle][aria-expanded="true"]');
      if (open) { open.setAttribute('aria-expanded', 'false'); open.focus(); }
    }
    if (nav.dataset.open === 'true') trapFocus(panel, e);
  });
  // the current category starts expanded in the drawer
  nav.querySelectorAll('.rdc-nav__item').forEach((item) => {
    if (item.querySelector('.rdc-nav__sub [aria-current="page"]')) item.querySelector('[data-rdc-sub-toggle]')?.setAttribute('aria-expanded', 'true');
  });
}

/* Native market selection keeps the current page, attribution and language. */
const countryForm = document.getElementById('rdc-country-form');
if (countryForm) {
  countryForm.addEventListener('submit', () => {
    const returnTo = countryForm.querySelector('[name="return_to"]');
    if (returnTo) returnTo.value = location.pathname + location.search + location.hash;
    document.cookie = 'rdc_lang=manual;path=/;max-age=31536000;SameSite=Lax';
  });
}

/* ------------------------------------------------------------ language disclosure (native form, works without JS) */
const lang = document.querySelector('[data-rdc-lang]');
if (lang) {
  document.addEventListener('click', (e) => { if (lang.open && !lang.contains(e.target)) lang.open = false; });
  lang.addEventListener('keydown', (e) => { if (e.key === 'Escape' && lang.open) { lang.open = false; lang.querySelector('summary').focus(); } });
  lang.addEventListener('submit', (e) => {
    const b = e.submitter;
    document.cookie = 'rdc_lang=manual;path=/;max-age=31536000;SameSite=Lax';   // an own choice always wins over rdc-locale-guess
    if (b) track('locale_changed', { from: RDC.locale, to: b.value, surface: 'header' });
  });
  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-rdc-lang-open]');
    if (!link) return;
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'auto' });
    lang.open = true;
    lang.querySelector('summary').focus();
  });
}

/* ------------------------------------------------------------ footer link groups: rendered closed (phones: no layout shift on short pages); opened from 768 px (CSS ::details-content where supported, JS everywhere) */
const footerMq = window.matchMedia('(min-width: 768px)');
const syncFooter = () => document.querySelectorAll('[data-rdc-footer-group]').forEach((d) => { d.open = footerMq.matches; });
syncFooter();
footerMq.addEventListener('change', syncFooter);

/* ------------------------------------------------------------ cart (drawer + page) */
const cartDialog = () => document.getElementById('rdc-cart');
const cartPage = () => document.querySelector('[data-rdc-cart-page]');

function sectionIdOf(el) {
  const s = el?.closest('.shopify-section');
  return s ? s.id.replace(/^shopify-section-/, '') : null;
}

function cartSections() {
  const ids = ['rdc-cart-drawer'];
  const page = cartPage();
  const pid = page && sectionIdOf(page);
  if (pid) ids.push(pid);
  return ids;
}

function parse(html) {
  return new DOMParser().parseFromString(html, 'text/html');
}

function applyCartSections(sections, count) {
  const dialog = cartDialog();
  if (dialog && sections['rdc-cart-drawer']) {
    const doc = parse(sections['rdc-cart-drawer']);
    const fresh = doc.getElementById('rdc-cart');
    if (fresh) {
      dialog.innerHTML = fresh.innerHTML;
      dialog.dataset.count = fresh.dataset.count;
    }
  }
  const page = cartPage();
  const pid = page && sectionIdOf(page);
  if (page && pid && sections[pid]) {
    const doc = parse(sections[pid]);
    const fresh = doc.querySelector('[data-rdc-cart-page]');
    if (fresh) page.innerHTML = fresh.innerHTML;
  }
  const n = Number(count ?? dialog?.dataset.count ?? 0);
  document.querySelectorAll('[data-rdc-cart-count]').forEach((node) => { node.textContent = n; node.dataset.count = n; });
  const labelTpl = dialog?.querySelector('[data-rdc-cart-label-tpl]');
  document.querySelectorAll('[data-rdc-cart-label]').forEach((node) => {
    if (labelTpl) node.textContent = labelTpl.textContent;
  });
}

async function addToCart(form) {
  const body = new FormData(form);
  body.append('sections', cartSections().join(','));
  body.append('sections_url', window.location.pathname);
  const res = await fetch(`${RDC.routes.cartAdd}.js`, { method: 'POST', body, headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } });
  const data = await res.json();
  if (!res.ok || data.status) throw new Error(data.description || data.message || RDC.strings.addError);
  const cart = await (await fetch(`${RDC.routes.cart}.js`, { headers: { Accept: 'application/json' } })).json();
  applyCartSections(data.sections || {}, cart.item_count);
  return { item: data, cart };
}
window.rdcAddToCart = addToCart;

async function changeLine(key, quantity) {
  const res = await fetch(`${RDC.routes.cartChange}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ id: key, quantity, sections: cartSections(), sections_url: window.location.pathname })
  });
  const data = await res.json();
  if (!res.ok || data.status) throw new Error(data.description || data.message || RDC.strings.cartError);
  applyCartSections(data.sections || {}, data.item_count);
  return data;
}

let cartBusy = false;
async function updateLineFrom(el, quantity) {
  const line = el.closest('[data-rdc-line]');
  if (!line || cartBusy) return;
  cartBusy = true;
  const root = line.closest('dialog, [data-rdc-cart-page]');
  root?.setAttribute('aria-busy', 'true');
  const wasDialog = root instanceof HTMLDialogElement;
  const index = Number(line.dataset.rdcLine);
  try {
    await changeLine(line.dataset.key, Math.max(0, quantity));
    track(quantity === 0 ? 'cart_line_removed' : 'cart_qty_changed', { new_qty: quantity });
    announce(RDC.strings.cartUpdated);
    const scope = wasDialog ? cartDialog() : cartPage();
    const next = scope?.querySelector(`[data-rdc-line="${index}"] [data-rdc-qty-input]`) || scope?.querySelector('[data-rdc-qty-input]') || scope?.querySelector('[data-rdc-close], a, button');
    next?.focus();
  } catch (err) {
    announce(err.message || RDC.strings.cartError);
  } finally {
    cartBusy = false;
    document.querySelectorAll('[aria-busy="true"]').forEach((n) => n.removeAttribute('aria-busy'));
  }
}

document.addEventListener('click', (e) => {
  const step = e.target.closest('[data-rdc-qty-step]');
  if (step) {
    const input = step.parentElement.querySelector('[data-rdc-qty-input]');
    const q = Number(input.value || 0) + Number(step.dataset.rdcQtyStep);
    input.value = Math.max(0, q);
    updateLineFrom(step, Math.max(0, q));
    return;
  }
  const remove = e.target.closest('[data-rdc-remove]');
  if (remove) { e.preventDefault(); updateLineFrom(remove, 0); }
});
document.addEventListener('change', (e) => {
  const input = e.target.closest('[data-rdc-qty-input]');
  if (input) updateLineFrom(input, Number(input.value || 0));
});
document.addEventListener('submit', (e) => {
  const btn = e.submitter;
  if (btn && btn.name === 'checkout') {
    const count = Number(cartDialog()?.dataset.count || 0);
    track('checkout_started', { line_count: count });
  }
});

/* ------------------------------------------------------------ "Mijn auto" garage state (MP08; only with the fitment flag on)
   Browser-only functional state: localStorage `rdc_garage_v1` = { version: 1, active, vehicles[≤3]: { vehicle_handle, display,
   chassis_code, source, added_at } } (Phase 3A garage contract). Never a licence plate. Everything read back is validated: a
   hand-edited or outdated value is repaired (redirects) or dropped, never trusted. No storage (private mode, blocked) → in-memory. */
const GARAGE_KEY = 'rdc_garage_v1';
const GARAGE_MAX = 3;
const HANDLE_RE = /^[a-z0-9-]+\/[a-z0-9-]+\/[a-z0-9-]+$/;
const SOURCES = ['selector', 'registration', 'text'];
const MAKE_NAMES = { audi: 'Audi', bmw: 'BMW', 'mercedes-benz': 'Mercedes-Benz' };
const slugify = (s) => String(s || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
/* make name as written at the start of a stored label ("Alfa Romeo Giulietta 2012" → "Alfa Romeo" for slug alfa-romeo) */
function makePrefix(display, slug) {
  const w = String(display || '').split(' ');
  for (let i = 1; i < w.length && i <= 3; i += 1) if (slugify(w.slice(0, i).join(' ')) === slug) return w.slice(0, i).join(' ');
  return null;
}
let garageMemory = null;

function emptyGarage() { return { version: 1, active: null, vehicles: [] }; }

function cleanGarage(raw, redirects = {}) {
  const out = emptyGarage();
  if (!raw || typeof raw !== 'object' || raw.version !== 1 || !Array.isArray(raw.vehicles)) return out;
  const seen = new Set();
  for (const v of raw.vehicles) {
    if (!v || typeof v !== 'object') continue;
    const handle = redirects[v.vehicle_handle] || v.vehicle_handle;
    if (typeof handle !== 'string' || !HANDLE_RE.test(handle) || seen.has(handle)) continue;
    if (typeof v.display !== 'string' || !v.display.trim() || v.display.length > 140) continue;
    const chassis = typeof v.chassis_code === 'string' && /^[A-Z0-9]{1,8}$/.test(v.chassis_code) ? v.chassis_code : null;
    seen.add(handle);
    out.vehicles.push({ vehicle_handle: handle, display: v.display.trim(), chassis_code: chassis,
      source: SOURCES.includes(v.source) ? v.source : 'selector', added_at: typeof v.added_at === 'string' ? v.added_at.slice(0, 10) : '' });
    if (out.vehicles.length === GARAGE_MAX) break;
  }
  const active = redirects[raw.active] || raw.active;
  out.active = out.vehicles.some((v) => v.vehicle_handle === active) ? active : (out.vehicles[0]?.vehicle_handle || null);
  return out;
}

function readGarage() {
  const redirects = RDC.vehicle?.redirects || {};
  if (garageMemory) return cleanGarage(garageMemory, redirects);
  let raw = null;
  try { raw = JSON.parse(window.localStorage.getItem(GARAGE_KEY) || 'null'); } catch (e) { raw = null; }
  const clean = cleanGarage(raw, redirects);
  // repair stored state once (redirected or invalid entries) so every surface sees the same canonical data
  if (raw && JSON.stringify(raw) !== JSON.stringify(clean)) writeGarage(clean, false);
  return clean;
}

function writeGarage(state, notify = true) {
  const clean = cleanGarage(state, RDC.vehicle?.redirects || {});
  try {
    if (!clean.vehicles.length) window.localStorage.removeItem(GARAGE_KEY);
    else window.localStorage.setItem(GARAGE_KEY, JSON.stringify(clean));
    garageMemory = null;
  } catch (e) {
    garageMemory = clean;                      // storage unavailable: keep it for this page only
    window.rdcGarageMemory = clean;
  }
  if (notify) document.dispatchEvent(new CustomEvent('rdc:garage-change', { detail: clean }));
  return clean;
}

const garage = {
  max: GARAGE_MAX,
  read: readGarage,
  write: writeGarage,
  active() { const g = readGarage(); return g.vehicles.find((v) => v.vehicle_handle === g.active) || null; },
  add(vehicle) {
    const g = readGarage();
    if (g.vehicles.some((v) => v.vehicle_handle === vehicle.vehicle_handle)) { g.active = vehicle.vehicle_handle; return { ok: true, state: writeGarage(g), existed: true }; }
    if (g.vehicles.length >= GARAGE_MAX) return { ok: false, reason: 'full', state: g };
    g.vehicles.push({ ...vehicle, added_at: new Date().toISOString().slice(0, 10) });
    g.active = vehicle.vehicle_handle;
    return { ok: true, state: writeGarage(g) };
  },
  setActive(handle) { const g = readGarage(); if (g.vehicles.some((v) => v.vehicle_handle === handle)) g.active = handle; return writeGarage(g); },
  remove(handle) {
    const g = readGarage();
    g.vehicles = g.vehicles.filter((v) => v.vehicle_handle !== handle);
    if (g.active === handle) g.active = g.vehicles[0]?.vehicle_handle || null;
    return writeGarage(g);
  },
  clear() { return writeGarage(emptyGarage()); },
  persistent() { return !garageMemory; },
  makeOf(handle) { return String(handle || '').split('/')[0]; },
  familyOf(handle) { return String(handle || '').split('/').slice(0, 2).join('/'); },
  makeName(slug) {
    if (MAKE_NAMES[slug]) return MAKE_NAMES[slug];
    const v = readGarage().vehicles.find((x) => this.makeOf(x.vehicle_handle) === slug);
    return (v && makePrefix(v.display, slug)) || slug;
  },
  /* compact header label: "A-Klasse · W177" — make and year range are dropped, the chassis code only when source-backed */
  shortLabel(v) {
    if (!v) return '';
    const make = MAKE_NAMES[this.makeOf(v.vehicle_handle)] || makePrefix(v.display, this.makeOf(v.vehicle_handle));
    let s = v.display.replace(/\s·\s[A-Z0-9]{1,8}$/, '');
    if (make && s.startsWith(`${make} `)) s = s.slice(make.length + 1);
    s = s.replace(/\s\d{4}–\d{4}$/, '');
    return v.chassis_code ? `${s} · ${v.chassis_code}` : s;
  }
};

function renderGarageHeader() {
  const v = RDC.vehicle ? garage.active() : null;
  document.querySelectorAll('[data-rdc-garage-open][data-surface="header"]').forEach((btn) => {
    const label = btn.querySelector('[data-rdc-garage-label]');
    const prefix = btn.querySelector('[data-rdc-garage-prefix]');
    const dot = btn.querySelector('[data-rdc-garage-dot]');
    if (!label) return;
    if (!label.dataset.empty) label.dataset.empty = label.textContent;
    label.textContent = v ? garage.shortLabel(v) : label.dataset.empty;
    if (prefix) prefix.hidden = !v;
    if (dot) dot.hidden = !v;
    btn.dataset.hasVehicle = String(!!v);
  });
}

function decodeText(s) {
  if (!s.includes('&')) return s;
  const box = document.createElement('textarea');
  box.innerHTML = s;                                   // inert: textarea content is never parsed as markup
  return box.value;
}

/* Card / predictive-search status lines: only verified states, only when the published index holds relations for this page. */
const CARD_KEYS = { fits: 'cardFits', conditional: 'cardConditional', no_fit: 'cardNoFit' };
function renderFitSlots(scope = document) {
  const V = RDC.vehicle;
  if (!V || !V.activeRelations || !V.relations || !V.products) return;
  const v = garage.active();
  scope.querySelectorAll('[data-rdc-fitment-slot="card"],[data-rdc-fitment-slot="search"]').forEach((slot) => {
    const p = V.products[slot.dataset.productId];
    const rel = v && p && garage.makeOf(v.vehicle_handle) === p.make ? (V.relations[p.hw] || {})[v.vehicle_handle] : null;
    const key = rel && CARD_KEYS[rel.s];
    slot.hidden = !key;
    slot.dataset.state = key ? rel.s : '';
    slot.textContent = key ? decodeText(RDC.strings[key] || '') : '';
  });
}

let vehicleModule = null;
function loadVehicleModule() {
  if (!RDC.vehicle) return Promise.reject(new Error('vehicle feature off'));
  vehicleModule = vehicleModule || import(RDC.vehicle.module);
  return vehicleModule;
}

if (RDC.vehicle) {
  renderGarageHeader();
  renderFitSlots();
  document.addEventListener('rdc:garage-change', () => { renderGarageHeader(); renderFitSlots(); });
  window.addEventListener('storage', (e) => { if (e.key === GARAGE_KEY) document.dispatchEvent(new CustomEvent('rdc:garage-change', { detail: readGarage() })); });
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-rdc-garage-open]');
    if (!btn) return;
    e.preventDefault();
    const dialog = document.getElementById('rdc-garage');
    if (!dialog) return;
    openDialog(dialog, btn);
    const surface = btn.dataset.surface || 'header';
    loadVehicleModule().then((m) => m.open(dialog, { surface, intent: btn.hasAttribute('data-rdc-fit-choose') || btn.hasAttribute('data-rdc-fit-switch') ? 'add' : 'manage' }))
      .catch(() => {
        vehicleModule = null;                       // allow a retry on the next click
        const body = dialog.querySelector('[data-rdc-garage-body]');
        let msg = '';
        try { msg = decodeText(JSON.parse(dialog.querySelector('[data-rdc-garage-strings]').textContent).loadError || ''); } catch (err) { msg = ''; }
        if (body) { body.replaceChildren(); const p = document.createElement('p'); p.className = 'rdc-notice rdc-notice--error'; p.setAttribute('role', 'alert'); p.textContent = msg; body.append(p); }
      });
  });
}

/* Shared API for the page modules (rdc-product.js, rdc-collection.js, rdc-search.js, rdc-vehicle.js). Module scripts run in document order,
   so this object exists before they execute; a relative `import` would load a second copy under a different URL. */
window.rdc = { RDC, track, announce, openDialog, closeDialog, addToCart, trapFocus, setBackgroundInert,
  vehicle: RDC.vehicle ? { garage, renderFitSlots, loadModule: loadVehicleModule } : null };

/* ---------------------------------------------------------------- campaign stamp (19-09-2026)
   Where an order came from, without a tracking cookie. The campaign labels in the landing URL — and the bare hostname of an
   external referrer — are written to the cart as attributes, so they ride along to the order (note_attributes) for every customer,
   including the ones who decline cookies: the cart cookie Shopify sets anyway is the only storage involved.
   Only labels are kept. Never a click id (gclid/ttclid identify a person), never a full referrer URL, never a query string.
   First touch is written once and never overwritten; last touch is refreshed on every new external arrival, because the last
   campaign before the order is what a CAC calculation needs. Internal navigation writes nothing, so a visit costs one call. */
const RDC_UTM = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

function campaignStamp() {
  let params;
  try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
  const tags = {};
  RDC_UTM.forEach((k) => { const v = params.get(k); if (v) tags[k] = v.slice(0, 120); });

  let host = '';
  try {
    const ref = document.referrer ? new URL(document.referrer) : null;
    if (ref && ref.hostname && ref.hostname !== window.location.hostname) host = ref.hostname.replace(/^www\./, '');
  } catch (e) { host = ''; }

  if (!Object.keys(tags).length && !host) return;        // internal click or untagged direct visit — nothing to record

  const base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || RDC.routes.root || '/';
  const now = new Date().toISOString().slice(0, 19) + 'Z';
  const last = { ...tags };
  if (host) last.referrer_host = host;
  last.landing_page = window.location.pathname.slice(0, 160);

  fetch(`${base}cart.js`, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .then((cart) => {
      const have = (cart && cart.attributes) || {};
      const write = { ...last };
      if (!have.first_seen_at) {                         // first touch: written once per cart, then left alone
        write.first_source = tags.utm_source || host || 'direct';   // deliberately one field: the order page stays readable
        if (tags.utm_campaign) write.first_campaign = tags.utm_campaign;
        write.first_seen_at = now;
      }
      const changed = Object.keys(write).some((k) => have[k] !== write[k]);
      if (!changed) return null;
      return fetch(`${base}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: write }),
      });
    })
    .then(() => { document.dispatchEvent(new CustomEvent('rdc:campaign-stamped')); })
    .catch(() => { /* measurement must never break the shop */ });
}

/* the product form carries the same labels as hidden line-item properties: an express checkout (Apple Pay, Shop Pay) starts from the
   product form and skips the cart, so cart attributes alone would lose exactly the orders the express button wins. */
function campaignFillForm() {
  const base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || RDC.routes.root || '/';
  const inputs = document.querySelectorAll('[data-rdc-campaign-prop]');
  if (!inputs.length) return;
  fetch(`${base}cart.js`, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .then((cart) => {
      const a = (cart && cart.attributes) || {};
      inputs.forEach((input) => {
        const v = a[input.dataset.rdcCampaignProp];
        if (v) { input.value = String(v).slice(0, 120); input.disabled = false; } else { input.value = ''; input.disabled = true; }
      });
    })
    .catch(() => { /* leave the hidden inputs disabled */ });
}

campaignStamp();
document.addEventListener('rdc:campaign-stamped', campaignFillForm);
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', campaignFillForm);
else campaignFillForm();
